import { isValidDate } from '@frontend/common';
import { ObjectBadgeMapper, isUUID } from '@frontend/components/common';
import { ExportModal } from '@frontend/components/modal';
import { ICONS } from '@frontend/icons';
import { CommonMessage } from '@frontend/lang';
import { ModalType } from '@frontend/modal';
import { Report } from '@frontend/report/types';
import { removeActiveFilter } from '@frontend/repository';
import { Action, ThunkDispatch } from '@reduxjs/toolkit';
import { format } from 'date-fns';
import React from 'react';
import { SetURLSearchParams } from 'react-router-dom';

import { FilterModal } from './filter-modal/filter-modal.component';
import { useInsaneFilter } from './insane-filter.controller';
import { InsaneSearch } from './insane-search/insane-search.component';

const PLURAL_KEYS = ['senders', 'receivers', 'owners', 'users', 'products'];

export interface InsaneFilterProps {
    searchKeys?: string[];
    filterKeys?: string[];
    listId: string;
    dispatch: ThunkDispatch<any, any, Action>;
    extraProps?: Map<string, object>;
    displayName?: Map<string, React.ReactNode>;
    queryKeyMap?: Map<string, string>;
    export?: {
        overwriteExport?: () => void;
        modalId?: string;
        report?: Report;
        data?: { [key: string]: any };
        dataKeys?: string[];
    };
    trailingActions?: React.ReactNode;
}

export const InsaneFilter = (props: InsaneFilterProps) => {
    const viewProps = useInsaneFilter(props);

    const handleExport = () => {
        if (!props.export) return;
        if (props.export.overwriteExport) {
            props.export.overwriteExport();
            return;
        }
        if (!props.export.modalId || !props.export.report) return;

        viewProps.openModal(props.export.modalId, () => (
            <ExportModal
                modalId={props.export!.modalId!}
                report={props.export!.report!}
                data={props.export?.data}
                searchKeys={props.searchKeys}
                dataKeys={props.export?.dataKeys}
            />
        ));
    };

    return (
        <div
            id='table-filter'
            className='border-border-default flex justify-between gap-4 self-stretch overflow-hidden border-b p-4'>
            <div className='flex w-full flex-row flex-wrap gap-4'>
                {props.searchKeys && props.searchKeys.length > 0 && (
                    <InsaneSearch
                        dispatch={props.dispatch}
                        listStore={viewProps.list}
                        listId={props.listId}
                        searchKeys={props.searchKeys}
                        displayName={props.displayName}
                    />
                )}

                {viewProps.list && viewProps.list.activeFilters && Object.values(viewProps.list.activeFilters).length > 0 && (
                    <div className='inline-flex items-center justify-start gap-2 self-stretch'>
                        {Object.entries(viewProps.list.activeFilters).map(([key, values]) => (
                            <div
                                key={key}
                                className='inline-flex items-center justify-start gap-1'>
                                {values.map((value) => {
                                    const finalKey = props.queryKeyMap?.get(key) || key;
                                    const displayName = props.displayName?.get(key);
                                    const chipKey = `${key}:${value}`;
                                    if ((isUUID(value) && key.endsWith('_id')) || PLURAL_KEYS.includes(key)) {
                                        return (
                                            <ObjectBadgeMapper
                                                id={value}
                                                type={key}
                                                key={chipKey}
                                                className='bg-brand-muted text-background-surface inline-flex flex-nowrap items-center justify-center gap-1 rounded-full px-3 py-1 font-medium'
                                                disableClick
                                                clearable={{
                                                    onClear: () => onRemove(props.dispatch, viewProps.setSearchParams, props.listId, key, value, finalKey)
                                                }}
                                            />
                                        );
                                    } else if (isValidDate(value)) {
                                        return (
                                            <div
                                                key={chipKey}
                                                className='bg-brand-muted inline-flex flex-nowrap items-center justify-center gap-1 rounded-full px-3 py-1'>
                                                <div className='text-background-surface flex flex-nowrap items-center gap-1 text-base font-medium'>
                                                    {key === 'creation_timestamp' || key === 'update_timestamp' ? (
                                                        <ICONS.BUTTON.CALENDAR className='text-background-surface size-full' />
                                                    ) : (
                                                        <span>{displayName ?? (key.charAt(0).toUpperCase() + key.slice(1)).replace(/_/g, ' ')}:</span>
                                                    )}

                                                    <span>{format(new Date(value), 'dd/MM/yyyy')}</span>
                                                </div>
                                                <div
                                                    onClick={() => onRemove(props.dispatch, viewProps.setSearchParams, props.listId, key, value, finalKey)}
                                                    className='flex size-4 cursor-pointer items-center justify-center'>
                                                    <ICONS.BUTTON.CLOSE_2 className='text-background-surface size-full' />
                                                </div>
                                            </div>
                                        );
                                    }
                                    return (
                                        <div
                                            key={chipKey}
                                            className='bg-brand-muted inline-flex flex-nowrap items-center justify-center gap-1 rounded-full px-3 py-1'>
                                            <div className='text-background-surface flex flex-nowrap gap-1 text-sm font-medium'>
                                                <span>{displayName ?? (key.charAt(0).toUpperCase() + key.slice(1)).replace(/_/g, ' ')}:</span>
                                                <span>{value}</span>
                                            </div>
                                            <div
                                                onClick={() => onRemove(props.dispatch, viewProps.setSearchParams, props.listId, key, value, finalKey)}
                                                className='flex size-4 cursor-pointer items-center justify-center'>
                                                <ICONS.BUTTON.CLOSE_2 className='text-background-surface size-full' />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ))}
                    </div>
                )}
            </div>
            {(props.export || (props.filterKeys && props.filterKeys.length > 0) || props.trailingActions) && (
                <div className='flex gap-2'>
                    {props.export && (
                        <div
                            onClick={handleExport}
                            className='bg-background-surface hover:bg-background-default outline-border-default flex h-11 w-fit cursor-pointer items-center justify-start gap-2 rounded-lg px-4 py-3 outline-1 outline-offset-[-1px]'>
                            <div className='text-brand-muted justify-start text-base font-medium text-nowrap'>{CommonMessage.BUTTONS.EXPORT}</div>
                        </div>
                    )}
                    {props.filterKeys && props.filterKeys.length > 0 && (
                        <div
                            onClick={() => {
                                if (!props.filterKeys || props.filterKeys.length === 0) return;
                                viewProps.openModal(
                                    viewProps.filterModalId,
                                    () => (
                                        <FilterModal
                                            dispatch={props.dispatch}
                                            listId={props.listId}
                                            filterKeys={props.filterKeys!}
                                            extraProps={props.extraProps}
                                            displayName={props.displayName}
                                            queryKeyMap={props.queryKeyMap}
                                        />
                                    ),
                                    {},
                                    { type: ModalType.PANE, className: 'max-w-[450px]' }
                                );
                            }}
                            className='bg-background-surface hover:bg-background-default outline-border-default flex h-11 w-fit cursor-pointer items-center justify-start gap-2 rounded-lg px-4 py-3 outline-1 outline-offset-[-1px]'>
                            <ICONS.BUTTON.FILTER className='text-brand-muted size-5' />
                        </div>
                    )}
                    {props.trailingActions}
                </div>
            )}
        </div>
    );
};

const onRemove = (
    dispatch: ThunkDispatch<any, any, Action>,
    setSearchParams: SetURLSearchParams,
    listId: string,
    key: string,
    value: string,
    paramKey: string
) => {
    dispatch(removeActiveFilter({ listId: listId, key, value }));
    setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        const actualKey = key === 'search' ? 'All' : paramKey;
        const existing = params.getAll(actualKey);
        if (existing) {
            const values = existing.filter((v) => v !== value);
            if (values.length > 0) {
                params.delete(actualKey);
                values.forEach((v) => params.append(actualKey, v));
            } else {
                params.delete(actualKey);
            }
        }

        return params;
    });
};
